import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUpRight, type LucideIcon } from 'lucide-react';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';
import { Icon } from './Icon';

const m = defineMessages({
  openSection: {
    id: 'common.ui.kpiCard.openSection',
    defaultMessage: 'Открыть раздел: {label}',
    description: 'Всплывающая подсказка нажимаемой KPI-карточки, ведущей в другой раздел; {label} — подпись карточки',
  },
  filterBelow: {
    id: 'common.ui.kpiCard.filterBelow',
    defaultMessage: 'Показать ниже на странице',
    description: 'Всплывающая подсказка нажимаемой KPI-карточки, которая фильтрует данные ниже на этой же странице',
  },
});

export type KpiVariant = 'neutral' | 'sage' | 'hero' | 'plain';

/**
 * Что делает нажатие (единое правило для всех KPI):
 * - `navigate` — уводит в другой раздел, иконка ↗;
 * - `filter` — фильтрует или прокручивает к блоку на этой же странице, иконка ↓;
 * - нет действия — карточка только показывает число: без иконки и без hover.
 */
export type KpiAction = 'navigate' | 'filter';

export interface KpiCardProps {
  /** Одно-четыре слова. */
  label: string;
  value: ReactNode;
  unit?: string;
  /**
   * ОДНА нижняя строка: дельта, источник или охват — но не две сразу.
   * Анатомия карточки зафиксирована: label → value/unit → одна нижняя строка.
   */
  note?: ReactNode;
  icon?: LucideIcon;
  variant?: KpiVariant;
  /**
   * Смысловой тон ЧИСЛА (ADR-0009): `warning` — после срока, недоподача.
   * Красного тона у KPI нет: «после срока» и «без отчёта» — про данные, а не
   * критичное состояние объекта.
   */
  tone?: 'warning';
  /**
   * Вид действия. По умолчанию выводится из пропсов: `to` → `navigate`,
   * `onClick` → `filter`. Без `to` и `onClick` игнорируется — карточка не
   * выглядит кликабельной.
   */
  action?: KpiAction;
  /** Состояние переключателя (карточка-фильтр включена/выключена). */
  pressed?: boolean;
  /** Карточка — переход на страницу (`Link`). */
  to?: string;
  /** Карточка — действие на месте (прокрутка к блоку, вкладка). Важнее `to`. */
  onClick?: () => void;
  /** Подсказка «как считается» в правом верхнем углу (вне кликабельной зоны). */
  info?: ReactNode;
  /** Доступное имя действия для `to`/`onClick`, если подписи мало. */
  actionLabel?: string;
}

/**
 * `hero` — единственный доминирующий градиентный план в ряду. Не берите его
 * просто потому, что число большое: hero — про решение, а не про размер.
 */
export function KpiCard({ label, value, unit, note, icon, variant = 'neutral', tone, to, onClick, info, actionLabel, action, pressed }: KpiCardProps) {
  const t = useT();
  const isHero = variant === 'hero';
  const interactive = Boolean(onClick || to);
  const kind: KpiAction | null = interactive ? (action ?? (onClick ? 'filter' : 'navigate')) : null;
  const hint = kind === 'navigate' ? t(m.openSection, { label }) : kind === 'filter' ? t(m.filterBelow) : undefined;
  const cls = `kpi kpi--${variant} ${isHero ? 'on-green' : ''} ${tone ? `kpi--${tone}` : ''} ${kind ? `kpi--action kpi--${kind}` : ''} ${info ? 'kpi--info' : ''}`;
  const body = (
    <>
      <div className="kpi__label">
        {icon ? <Icon icon={icon} size={16} /> : null}
        <span>{label}</span>
      </div>
      <div className="kpi__value">
        {value}
        {unit ? <span className="kpi__unit">{unit}</span> : null}
      </div>
      <div className="kpi__spacer" />
      {note ? <div className="kpi__note">{note}</div> : null}
      {kind ? (
        <span className="kpi__action" aria-hidden="true">
          <Icon icon={kind === 'navigate' ? ArrowUpRight : ArrowDown} size={16} />
        </span>
      ) : null}
    </>
  );
  const card = onClick ? (
    <button type="button" className={cls} onClick={onClick} aria-label={actionLabel} aria-pressed={pressed} title={hint}>{body}</button>
  ) : to ? (
    <Link className={cls} to={to} aria-label={actionLabel} title={hint}>{body}</Link>
  ) : (
    <div className={cls}>{body}</div>
  );
  if (!info) return card;
  return (
    <div className="kpi-wrap">
      {card}
      <span className="kpi__info">{info}</span>
    </div>
  );
}
